import { Column } from "primereact/column";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { ChangeEvent, Component } from "react";
import { PageLayout } from "../../../components/ui";
import { Button } from "primereact/button";
import ExtendedInputText from "../../../components/ExtendedInputText";
import { toastService } from "../../../services/toastService";
import { utils } from "../../../utils/utils";
import { fileService } from "../../../services/FileService";
import { ExtendedImage } from "../../../components/Image/ExtendedImage";
import _ from "lodash";
import { SortOrderType } from "../../../models/SearchRequestModel";
import { merchantVendorApiService } from "../../../services/api/merchants/MerchantVendorApiService";
import { UpdateMerchantVendorRequestModel, MerchantVendorCategoryResponseModel } from "../../../models/MerchantVendor";
import { paginatorTemplate } from "../../Paginator";
import { FilterMatchMode } from "primereact/api";
import { InputTextFilerTemplate } from "../../Operations/TransactionFilterTemplates";
import { File, FilterX, Image as ImageIcon } from "lucide-react";

interface VendorCategoriesProps {
    merchantId: number;
}

interface VendorCategoriesState {
    isLoading: boolean;
    vendorCategories: MerchantVendorCategoryResponseModel[];
    imageSourcesVendorCategories: Map<string, string>;
    totalRecords: number;
    rows: number;
    editRowId: number | null;
    showFile: any;
    backupRowData: MerchantVendorCategoryResponseModel | null;
    filters: DataTableFilterMeta | undefined;
}

export default class VendorCategories extends Component<VendorCategoriesProps, VendorCategoriesState> {
    imageInput: HTMLInputElement | null = null;
    initializeFilters = {
        code: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        nameBG: { value: null, matchMode: FilterMatchMode.CONTAINS },
        nameEN: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    constructor(props: VendorCategoriesProps | Readonly<VendorCategoriesProps>) {
        super(props);

        this.state = {
            isLoading: true,
            vendorCategories: [],
            imageSourcesVendorCategories: new Map<string, string>(),
            totalRecords: 0,
            rows: 10,
            editRowId: null,
            showFile: null,
            backupRowData: null,
            filters: this.initializeFilters,
        };
    }

    async componentDidMount() {
        this.loadVendorsCategoriesData();
    }

    loadVendorsCategoriesData = async () => {
        this.setState({ isLoading: true });
        await this.loadVendorsCategories();
    };

    loadVendorsCategories = async () => {
        const vendorCategories = await merchantVendorApiService.getVendorCategories();
        if (!vendorCategories.success) {
            toastService.error(vendorCategories.message);
            this.setState({ isLoading: false });
            return;
        }

        this.setState(
            { vendorCategories: vendorCategories.data, totalRecords: vendorCategories.data.length, isLoading: false },
            () => {
                this.preLoadVendorsCategoriesImages();
            }
        );
    };

    preLoadVendorsCategoriesImages = async () => {
        const { vendorCategories, imageSourcesVendorCategories } = this.state;
        const updatedImageSources = new Map(imageSourcesVendorCategories);

        vendorCategories.forEach((category) => {
            if (category.logoFile && !updatedImageSources.has(category.logoFile)) {
                this.loadFile(category.logoFile)
                    .then((src) => {
                        updatedImageSources.set(category.logoFile!, src);
                        this.setState({ imageSourcesVendorCategories: new Map(updatedImageSources) });
                    })
                    .catch(() => {
                        console.error(`Failed to load image for ${category.logoFile}`);
                    });
            }
        });
    };

    loadFile = async (fileStorageId: string): Promise<string> => {
        if (fileStorageId != null || fileStorageId != "") {
            const imagePath = await fileService.GetFile(fileStorageId);
            if (!imagePath.success) {
                toastService.error(imagePath.message);
                this.setState({ isLoading: false });
                return "";
            }
            return utils.appendBase64Data(imagePath.data, fileStorageId);
        }
        return "";
    };

    saveRow = async (newData: MerchantVendorCategoryResponseModel) => {
        this.setState({ isLoading: true });
        const request: UpdateMerchantVendorRequestModel = {
            id: newData.id,
            nameBG: newData.nameBG!,
            nameEN: newData.nameEN!,
            logoFile:
                newData.logoFile && newData.logoFile.includes(".")
                    ? utils.removeBase64Data(this.state.imageSourcesVendorCategories.get(newData.logoFile) ?? "")
                    : newData.logoFile!,
        };
        const result = await merchantVendorApiService.updateVendorCategory(request);
        if (!result.success) {
            toastService.error(result.message);
            this.setState({ isLoading: false });
            return;
        }
        toastService.success(result.message);
        this.setState({ editRowId: null, backupRowData: null, showFile: null });
        await this.loadVendorsCategoriesData();
    };

    editRow = (rowData: MerchantVendorCategoryResponseModel) => {
        this.setState({
            editRowId: rowData.id,
            backupRowData: { ...rowData },
        });
    };

    cancelEdit = () => {
        const { editRowId, backupRowData, vendorCategories } = this.state;

        if (editRowId && backupRowData) {
            this.setState({
                vendorCategories: vendorCategories.map((vendor) => (vendor.id === editRowId ? backupRowData : vendor)),
                editRowId: null,
                backupRowData: null,
                showFile: null,
            });
        }
    };

    renderEditableField = (field: keyof MerchantVendorCategoryResponseModel, rowData: MerchantVendorCategoryResponseModel) => {
        const value = rowData[field] as string;
        return this.state.editRowId === rowData.id ? (
            <ExtendedInputText type="text" name={field} value={value || ""} onChange={(value) => this.onInputChange(field, value, rowData)} />
        ) : (
            rowData[field]
        );
    };

    onInputChange = (fieldName: string, value: string, rowData: MerchantVendorCategoryResponseModel) => {
        const updatedVendorServices = this.state.vendorCategories.map((vendor) => {
            if (vendor.id === rowData.id) {
                return { ...vendor, [fieldName]: value };
            }
            return vendor;
        });
        this.setState({ vendorCategories: updatedVendorServices });
    };

    onImageSelected = async (event: ChangeEvent<HTMLInputElement>, editorCallback: (val: any) => void) => {
        const documentFile = event.target.files && event.target.files[0];

        if (!documentFile) return;

        const allowedTypes = ["image/jpeg", "image/png", "image/svg+xml"];
        if (!allowedTypes.includes(documentFile.type)) {
            toastService.error("Only JPEG, PNG, and SVG files are allowed.", "File Type");
            if (this.imageInput) this.imageInput.value = "";
            return;
        }

        if (documentFile.size > 1024 * 1024) {
            toastService.error("File size exceeds 1MB.", "File Size");
            if (this.imageInput) this.imageInput.value = "";
            return;
        }

        if (!utils.checkCharacterlength(documentFile.name)) {
            toastService.error("File Name Exceeds 128 Characters.", "File Name");
            if (this.imageInput) this.imageInput.value = "";
            return;
        }

        const base64 = await utils.convertFileToBase64(documentFile);
        editorCallback(base64);
        this.setState({ showFile: utils.appendBase64Data(base64 as string) });
    };

    renderLogoFile = (rowData: MerchantVendorCategoryResponseModel) => {
        const imageSrc = this.state.imageSourcesVendorCategories.get(rowData.logoFile!) || undefined;
        if (this.state.editRowId === rowData.id) {
            return (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <Button label="Choose" className="ms-1" size="small" icon={<File size={16} />} onClick={() => this.imageInput?.click()} />
                    <input
                        ref={(el) => (this.imageInput = el)}
                        type="file"
                        style={{ display: "none" }}
                        accept="image/jpeg, image/png, image/svg+xml"
                        onChange={(event) =>
                            this.onImageSelected(event, (base64) => {
                                this.onInputChange("logoFile", base64, rowData);
                            })
                        }
                        required={true}
                    />
                    {imageSrc || this.state.showFile ? (
                        <img
                            src={imageSrc || this.state.showFile}
                            width={imageSrc ? "40px" : "30px"}
                            style={{ marginLeft: "10px" }}
                        />
                    ) : (
                        <div className="w-[30px] h-[30px] ml-2.5 flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 rounded">
                            <ImageIcon size={16} className="text-neutral-400" />
                        </div>
                    )}
                </div>
            );
        }
        return (
            <ExtendedImage
                width={imageSrc === undefined ? "30px" : "40px"}
                onPreview={() => this.loadFile(rowData.logoFile ?? "")}
                alt={rowData.logoFile}
                src={imageSrc || ""}
                preview={rowData.logoFile ? true : undefined}
            />
        );
    };

    renderActionButtons = (rowData: MerchantVendorCategoryResponseModel) => {
        if (rowData.isDeleted) {
            return null;
        }

        if (this.state.editRowId === rowData.id) {
            return (
                <>
                    <Button text className="p-2" onClick={() => this.saveRow(rowData)} label="Save" />
                    <Button text className="p-2" onClick={this.cancelEdit} label="Cancel" />
                </>
            );
        }

        return (
            <Button text onClick={() => this.editRow(rowData)} label="Edit" />
        );
    };

    renderHeader = () => (
        <span className="p-input-icon-left" style={{ display: "flex", justifyContent: "space-between" }}>
            <Button
                type="button"
                icon={<FilterX size={16} />}
                label="Clear"
                outlined
                onClick={() => {
                    this.setState({ filters: this.initializeFilters });
                }}
            />
        </span>
    );

    render() {
        return (
            <PageLayout noTitle>
                <DataTable
                                    header={this.renderHeader}
                                    editMode="row"
                                    dataKey="id"
                                    sortField="id"
                                    sortOrder={SortOrderType.Ascending}
                                    paginator
                                    paginatorTemplate={paginatorTemplate}
                                    rows={this.state.rows}
                                    totalRecords={this.state.totalRecords}
                                    value={this.state.vendorCategories}
                                    loading={this.state.isLoading}
                                    stripedRows={true}
                                    removableSort
                                    rowClassName={(rowData) => (rowData.isDeleted ? "table-secondary" : "")}
                                    filters={this.state.filters}
                                    onFilter={(e) => this.setState({ filters: e.filters })}
                                    filterDisplay="menu"
                                >
                                    <Column
                                        field="name"
                                        header="Name"
                                        sortable
                                        filter
                                        filterField="name"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />

                                    <Column
                                        field="nameBG"
                                        header="Name BG"
                                        body={(rowData) => this.renderEditableField("nameBG", rowData)}
                                        sortable
                                        filter
                                        filterField="nameBG"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />

                                    <Column
                                        field="nameEN"
                                        header="Name EN"
                                        body={(rowData) => this.renderEditableField("nameEN", rowData)}
                                        sortable
                                        filter
                                        filterField="nameEN"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />

                                    <Column field="logoFile" header="Logo File" body={this.renderLogoFile} />

                    <Column header="Actions" body={(rowData) => this.renderActionButtons(rowData)} />
                </DataTable>
            </PageLayout>
        );
    }
}
